// @ts-nocheck
/**
 * Content Library Browser Component
 * Carbon Design System DataTable for content browsing with search, filter, and pagination
 */

import React, { useState, useEffect } from 'react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  TableBatchActions,
  TableBatchAction,
  TableSelectAll,
  TableSelectRow,
  Button,
  Tag,
  OverflowMenu,
  OverflowMenuItem,
  Dropdown,
  DatePicker,
  DatePickerInput,
  Pagination,
  InlineLoading,
  Modal,
  TextInput,
  Tile,
  Grid,
  Column,
  FilterableMultiSelect,
  InlineNotification,
} from '@carbon/react';
import { Add, Edit, View, TrashCan, Download, Launch, Filter, Reset, Search, Copy } from '@carbon/icons-react';

import type {
  ContentLibraryItem,
  ContentLibraryState,
  ContentFilterCriteria,
  ContentSortCriteria,
  ContentType,
  ContentStatus,
  BulkAction,
  ContentPreviewOptions,
} from '../../../shared/interfaces/ContentManagementAdmin';

interface ContentLibraryBrowserProps {
  onContentSelect: (contentId: string) => void;
  onContentUpdate: (contentId: string, content: any) => void;
  onPreviewContent: (options: ContentPreviewOptions) => void;
  selectedContentId?: string;
}

const ContentLibraryBrowser: React.FC<ContentLibraryBrowserProps> = ({
  onContentSelect,
  onContentUpdate,
  onPreviewContent,
  selectedContentId,
}) => {
  const [state, setState] = useState<ContentLibraryState>({
    items: [],
    selectedItems: [],
    searchQuery: '',
    filterCriteria: {},
    sortCriteria: { field: 'lastModified', direction: 'desc' },
    pagination: {
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 0,
      totalPages: 0,
    },
    loading: true,
    error: null,
  });

  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<BulkAction['type']>('publish');

  // Content type and status options
  const contentTypeOptions = [
    { id: 'landing', text: 'Landing Page' },
    { id: 'email', text: 'Email Template' },
    { id: 'popup', text: 'Popup' },
    { id: 'banner', text: 'Banner' },
    { id: 'feature', text: 'Feature Content' },
    { id: 'template', text: 'Template' },
  ];

  const contentStatusOptions = [
    { id: 'draft', text: 'Draft' },
    { id: 'pending_approval', text: 'Pending Approval' },
    { id: 'approved', text: 'Approved' },
    { id: 'published', text: 'Published' },
    { id: 'archived', text: 'Archived' },
  ];

  // Load content data
  useEffect(() => {
    loadContentLibrary();
  }, [state.filterCriteria, state.sortCriteria, state.pagination.currentPage, state.searchQuery]);

  const loadContentLibrary = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const params = new URLSearchParams({
        page: state.pagination.currentPage.toString(),
        limit: state.pagination.itemsPerPage.toString(),
        search: state.searchQuery,
        sortField: state.sortCriteria.field,
        sortDirection: state.sortCriteria.direction,
        ...Object.fromEntries(
          Object.entries(state.filterCriteria).map(([key, value]) => [
            key,
            Array.isArray(value) ? value.join(',') : value,
          ])
        ),
      });

      const response = await fetch(`/api/v1/content-management/content/search?${params}`);

      if (!response.ok) {
        throw new Error('Failed to load content library');
      }

      const data = await response.json();

      setState(prev => ({
        ...prev,
        items: data.items || [],
        pagination: {
          ...prev.pagination,
          totalItems: data.totalItems || 0,
          totalPages: Math.ceil((data.totalItems || 0) / prev.pagination.itemsPerPage),
        },
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load content',
        loading: false,
      }));
    }
  };

  const handleSearch = (query: string) => {
    setState(prev => ({
      ...prev,
      searchQuery: query,
      pagination: { ...prev.pagination, currentPage: 1 },
    }));
  };

  const handleSort = (headerKey: string) => {
    setState(prev => ({
      ...prev,
      sortCriteria: {
        field: headerKey as any,
        direction: prev.sortCriteria.field === headerKey && prev.sortCriteria.direction === 'asc' ? 'desc' : 'asc',
      },
    }));
  };

  const handleFilter = (filters: Partial<ContentFilterCriteria>) => {
    setState(prev => ({
      ...prev,
      filterCriteria: { ...prev.filterCriteria, ...filters },
      pagination: { ...prev.pagination, currentPage: 1 },
    }));
  };

  const handleClearFilters = () => {
    setState(prev => ({
      ...prev,
      filterCriteria: {},
      pagination: { ...prev.pagination, currentPage: 1 },
    }));
  };

  const handleRowSelect = (contentId: string) => {
    onContentSelect(contentId);
  };

  const handleBulkAction = async (action: BulkAction['type'], comment?: string) => {
    if (state.selectedItems.length === 0) return;

    try {
      const response = await fetch('/api/v1/content-management/content/bulk-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: action,
          contentIds: state.selectedItems,
          comment,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} content`);
      }

      // Refresh the content library
      loadContentLibrary();

      // Clear selections
      setState(prev => ({ ...prev, selectedItems: [] }));
      setBulkActionModalOpen(false);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : `Failed to ${action} content`,
      }));
    }
  };

  const handleContentPreview = (item: ContentLibraryItem) => {
    onPreviewContent({
      contentId: item.id,
      previewMode: item.status === 'published' ? 'live' : 'draft',
      deviceType: 'desktop',
    });
  };

  const handleContentEdit = (item: ContentLibraryItem) => {
    onContentSelect(item.id);
    // Navigate to editor tab - this would be handled by parent component
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusTag = (status: ContentStatus) => {
    const statusConfig = {
      draft: { type: 'gray' as const, label: 'Draft' },
      pending_approval: { type: 'blue' as const, label: 'Pending Approval' },
      approved: { type: 'green' as const, label: 'Approved' },
      published: { type: 'blue' as const, label: 'Published' },
      archived: { type: 'red' as const, label: 'Archived' },
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <Tag type={config.type}>{config.label}</Tag>;
  };

  const getContentTypeTag = (type: ContentType) => {
    const typeConfig = {
      landing: 'Landing Page',
      email: 'Email',
      popup: 'Popup',
      banner: 'Banner',
      feature: 'Feature',
      template: 'Template',
    };

    return <Tag type="cool-gray">{typeConfig[type] || type}</Tag>;
  };

  // DataTable headers
  const headers = [
    { key: 'title', header: 'Title' },
    { key: 'type', header: 'Type' },
    { key: 'status', header: 'Status' },
    { key: 'author', header: 'Author' },
    { key: 'lastModified', header: 'Last Modified' },
    { key: 'version', header: 'Version' },
    { key: 'actions', header: 'Actions' },
  ];

  // Transform data for DataTable
  const rows = state.items.map(item => ({
    id: item.id,
    title: item.title,
    type: getContentTypeTag(item.type),
    status: getStatusTag(item.status),
    author: item.author,
    lastModified: formatDate(item.lastModified),
    version: `v${item.version}`,
    actions: (
      <OverflowMenu flipped>
        <OverflowMenuItem itemText="Preview" onClick={() => handleContentPreview(item)} />
        <OverflowMenuItem itemText="Edit" onClick={() => handleContentEdit(item)} />
        <OverflowMenuItem itemText="Duplicate" />
        <OverflowMenuItem itemText="Delete" isDelete />
      </OverflowMenu>
    ),
  }));

  const renderFilterPanel = () => (
    <Tile style={{ marginBottom: '1rem', padding: '1rem' }}>
      <Grid>
        <Column lg={4} md={4} sm={2}>
          <FilterableMultiSelect
            id="content-type-filter"
            titleText="Content Type"
            label="Filter by type"
            items={contentTypeOptions}
            itemToString={item => item?.text || ''}
            onChange={({ selectedItems }) =>
              handleFilter({
                type: selectedItems.map(item => item.id) as ContentType[],
              })
            }
          />
        </Column>

        <Column lg={4} md={4} sm={2}>
          <FilterableMultiSelect
            id="content-status-filter"
            titleText="Status"
            label="Filter by status"
            items={contentStatusOptions}
            itemToString={item => item?.text || ''}
            onChange={({ selectedItems }) =>
              handleFilter({
                status: selectedItems.map(item => item.id) as ContentStatus[],
              })
            }
          />
        </Column>

        <Column lg={4} md={4} sm={2}>
          <DatePicker
            datePickerType="range"
            onChange={(dates: [Date, Date]) => {
              const [start, end] = dates;
              if (start && end) {
                handleFilter({
                  dateRange: {
                    start: start.toISOString(),
                    end: end.toISOString(),
                  },
                });
              }
            }}
          >
            <DatePickerInput id="date-start" placeholder="Start date" labelText="Date range" />
            <DatePickerInput id="date-end" placeholder="End date" labelText="" />
          </DatePicker>
        </Column>

        <Column lg={4} md={4} sm={2}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: '100%' }}>
            <Button kind="secondary" renderIcon={Reset} onClick={handleClearFilters}>
              Clear Filters
            </Button>
            <Button kind="ghost" renderIcon={Filter} onClick={() => setFilterPanelOpen(false)}>
              Close
            </Button>
          </div>
        </Column>
      </Grid>
    </Tile>
  );

  if (state.error) {
    return (
      <div>
        <InlineNotification
          kind="error"
          title="Content Library Error"
          subtitle={state.error}
          onCloseButtonClick={() => setState(prev => ({ ...prev, error: null }))}
        />
        <Button kind="primary" renderIcon={Search} onClick={loadContentLibrary} style={{ marginTop: '1rem' }}>
          Retry Loading
        </Button>
      </div>
    );
  }

  return (
    <div className="content-library-browser">
      {/* Filter Panel */}
      {filterPanelOpen && renderFilterPanel()}

      {/* Content DataTable */}
      <DataTable
        rows={rows}
        headers={headers}
        isSortable
        onSort={handleSort}
        render={({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
          getToolbarProps,
          onInputChange,
          getSelectionProps,
          getBatchActionProps,
          selectedRows,
        }) => {
          // Update selected items when selection changes
          React.useEffect(() => {
            setState(prev => ({
              ...prev,
              selectedItems: selectedRows.map(row => row.id),
            }));
          }, [selectedRows]);

          return (
            <TableContainer>
              <TableToolbar {...getToolbarProps()}>
                <TableBatchActions {...getBatchActionProps()}>
                  <TableBatchAction
                    tabIndex={getBatchActionProps().shouldShowBatchActions ? 0 : -1}
                    renderIcon={Launch}
                    onClick={() => {
                      setBulkActionType('publish');
                      setBulkActionModalOpen(true);
                    }}
                  >
                    Publish
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={getBatchActionProps().shouldShowBatchActions ? 0 : -1}
                    renderIcon={TrashCan}
                    onClick={() => {
                      setBulkActionType('archive');
                      setBulkActionModalOpen(true);
                    }}
                  >
                    Archive
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={getBatchActionProps().shouldShowBatchActions ? 0 : -1}
                    renderIcon={Copy}
                    onClick={() => {
                      // Handle bulk duplicate
                    }}
                  >
                    Duplicate
                  </TableBatchAction>
                </TableBatchActions>

                <TableToolbarContent>
                  <TableToolbarSearch
                    defaultValue={state.searchQuery}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder="Search content..."
                  />
                  <Button kind="ghost" renderIcon={Filter} onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
                    Filters
                  </Button>
                  <Button kind="primary" renderIcon={Add} onClick={() => onContentSelect('')}>
                    New Content
                  </Button>
                </TableToolbarContent>
              </TableToolbar>

              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableSelectAll {...getSelectionProps()} />
                    {headers.map(header => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.loading ? (
                    <TableRow>
                      <TableCell colSpan={headers.length + 1}>
                        <InlineLoading description="Loading content..." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => (
                      <TableRow
                        key={row.id}
                        {...getRowProps({ row })}
                        isSelected={selectedContentId === row.id}
                        onClick={() => handleRowSelect(row.id)}
                      >
                        <TableSelectRow {...getSelectionProps({ row })} />
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                backwardText="Previous page"
                forwardText="Next page"
                itemsPerPageText="Items per page:"
                page={state.pagination.currentPage}
                pageNumberText="Page Number"
                pageRangeText={(current, total) => `of ${total} pages`}
                pageSize={state.pagination.itemsPerPage}
                pageSizes={[10, 20, 50, 100]}
                totalItems={state.pagination.totalItems}
                onChange={({ page, pageSize }) => {
                  setState(prev => ({
                    ...prev,
                    pagination: {
                      ...prev.pagination,
                      currentPage: page,
                      itemsPerPage: pageSize,
                    },
                  }));
                }}
              />
            </TableContainer>
          );
        }}
      />

      {/* Bulk Action Confirmation Modal */}
      <Modal
        open={bulkActionModalOpen}
        onRequestClose={() => setBulkActionModalOpen(false)}
        modalHeading={`${bulkActionType} Content`}
        modalLabel="Bulk Action"
        primaryButtonText={bulkActionType}
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleBulkAction(bulkActionType)}
      >
        <p>
          Are you sure you want to {bulkActionType} {state.selectedItems.length} content item(s)?
        </p>
        <TextInput
          id="bulk-action-comment"
          labelText="Comment (optional)"
          placeholder="Add a comment for this action..."
        />
      </Modal>
    </div>
  );
};

export default ContentLibraryBrowser;
